import { NewTripFormPersistData } from '../types/trip';

const STORAGE_KEY = 'newTripFormData';
const EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export class TripFormPersistenceService {
  static saveFormData(data: Partial<NewTripFormPersistData>): void {
    try {
      const persistData: NewTripFormPersistData = {
        customerMobile: '',
        customer: null,
        customerLookupMessage: '',
        showTripForm: false,
        isEditingCustomer: false,
        customerEditData: {
          name: '',
          email: '',
          gender: '',
          address: ''
        },
        tripData: {
          customerId: '',
          travelServiceDetailId: '',
          travelfleetDetailId: '',
          startDatetime: null,
          endDatetime: null,
          sourceCity: '',
          destinationCities: '',
          request: '',
          notes: '',
          driverId: '',
          carId: ''
        },
        errors: {},
        submitSuccess: false,
        createdTripCode: null,
        tripCreationResponse: null,
        timestamp: new Date().toISOString(),
        ...data
      };

      // Convert Date objects to ISO strings for storage
      const dataToStore = {
        ...persistData,
        tripData: {
          ...persistData.tripData,
          startDatetime: persistData.tripData.startDatetime instanceof Date 
            ? persistData.tripData.startDatetime.toISOString() 
            : persistData.tripData.startDatetime,
          endDatetime: persistData.tripData.endDatetime instanceof Date 
            ? persistData.tripData.endDatetime.toISOString() 
            : persistData.tripData.endDatetime
        }
      };

      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataToStore));
    } catch (error) {
      console.error('Error saving trip form data:', error);
    }
  }

  static getTripCreationData(): { tripCode: string | null; response: any | null } {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return { tripCode: null, response: null };

      const data = JSON.parse(stored);
      
      // Check if data is expired
      const dataTime = new Date(data.timestamp).getTime();
      const now = new Date().getTime();
      if (now - dataTime > EXPIRY_TIME) {
        this.clearFormData();
        return { tripCode: null, response: null };
      }

      return {
        tripCode: data.createdTripCode || null,
        response: data.tripCreationResponse || null
      };
    } catch (error) {
      console.error('Error getting trip creation data:', error);
      return { tripCode: null, response: null };
    }
  }

  static getFormData(): NewTripFormPersistData | null {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return null;
      }

      const data: NewTripFormPersistData = JSON.parse(stored);
      
      // Check if data is expired
      const dataTime = new Date(data.timestamp).getTime();
      const now = new Date().getTime();
      if (now - dataTime > EXPIRY_TIME) {
        this.clearFormData();
        return null;
      }

      // Convert ISO strings back to Date objects safely
      const processedData = {
        ...data,
        tripData: {
          ...data.tripData,
          startDatetime: data.tripData.startDatetime 
            ? (typeof data.tripData.startDatetime === 'string' 
                ? new Date(data.tripData.startDatetime) 
                : data.tripData.startDatetime)
            : null,
          endDatetime: data.tripData.endDatetime 
            ? (typeof data.tripData.endDatetime === 'string' 
                ? new Date(data.tripData.endDatetime) 
                : data.tripData.endDatetime)
            : null
        }
      };

      // Validate that dates are valid Date objects
      if (processedData.tripData.startDatetime && isNaN(processedData.tripData.startDatetime.getTime())) {
        processedData.tripData.startDatetime = null;
      }
      if (processedData.tripData.endDatetime && isNaN(processedData.tripData.endDatetime.getTime())) {
        processedData.tripData.endDatetime = null;
      }

      return processedData;
    } catch (error) {
      console.error('Error loading trip form data:', error);
      this.clearFormData();
      return null;
    }
  }

  static hasFormData(): boolean {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return false;

      const data = JSON.parse(stored);
      const dataTime = new Date(data.timestamp).getTime();
      const now = new Date().getTime();
      
      const hasData = (now - dataTime) <= EXPIRY_TIME;
      return hasData;
    } catch (error) {
      console.error('Error checking form data:', error);
      return false;
    }
  }

  static clearFormData(): void {
    try {
      const hadData = sessionStorage.getItem(STORAGE_KEY) !== null;
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing trip form data:', error);
    }
  }

  static preserveOnTabSwitch(): boolean {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return false;

      const data = JSON.parse(stored);
      
      // Don't preserve if trip was successfully created
      if (data.submitSuccess) {
        this.clearFormData();
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error checking if should preserve on tab switch:', error);
      return false;
    }
  }

  static shouldShowTripDetails(): boolean {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return false;

      const data = JSON.parse(stored);
      
      // Check if data is expired
      const dataTime = new Date(data.timestamp).getTime();
      const now = new Date().getTime();
      if (now - dataTime > EXPIRY_TIME) {
        this.clearFormData();
        return false;
      }

      // Show trip details if trip was successfully created
      return data.submitSuccess && data.createdTripCode && data.tripCreationResponse;
    } catch (error) {
      console.error('Error checking if should show trip details:', error);
      return false;
    }
  }

  static getCreatedTripCode(): string | null {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const data = JSON.parse(stored);
      return data.createdTripCode || null;
    } catch (error) {
      console.error('Error getting created trip code:', error);
      return null;
    }
  }

  static shouldNavigateToTrip(): { shouldNavigate: boolean; tripCode: string | null } {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return { shouldNavigate: false, tripCode: null };

      const data = JSON.parse(stored);
      
      // Check if data is expired
      const dataTime = new Date(data.timestamp).getTime();
      const now = new Date().getTime();
      if (now - dataTime > EXPIRY_TIME) {
        this.clearFormData();
        return { shouldNavigate: false, tripCode: null };
      }

      // Should navigate if trip was successfully created and we have a trip code
      const shouldNavigate = data.submitSuccess && data.createdTripCode && data.tripCreationResponse;
      return {
        shouldNavigate,
        tripCode: data.createdTripCode || null
      };
    } catch (error) {
      console.error('Error checking navigation state:', error);
      return { shouldNavigate: false, tripCode: null };
    }
  }

  static markNavigationComplete(): void {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return;

      const data = JSON.parse(stored);
      
      // Clear the navigation data but keep other form data
      const updatedData = {
        ...data,
        navigationCompleted: true,
        timestamp: new Date().toISOString()
      };

      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updatedData));
    } catch (error) {
      console.error('Error marking navigation complete:', error);
    }
  }
}
